/**
 * D196: authored verbs — the pack's own word, answered by the thing's own arms.
 *
 * @remarks
 * `act` is the one-object general case `use` is the two-object sugar for,
 * and its handler is `handleUse` with the pair lookup removed: the item's
 * `verbInteractions[verbToken]` arms are searched in authored order, the
 * first whose conditions ALL hold speaks and then applies its effects
 * (`withEffects` — the fold every event-then-effects seam shares). No arms
 * for the verb, or none whose conditions hold, is one refusal
 * (`verb_unanswered`), because to the player they are the same fact: this
 * thing does not answer that word right now. The arm an author writes
 * WITHOUT conditions is where the real refusal lives, and the slot is what
 * speaks when they wrote none.
 *
 * D229 (E22): the arms are read WITH THE COMMAND IN HAND, as a thing's
 * `unsupportedVerbNarratives` have been since D219 — the conditions and the
 * lines alike. Before this the only question an arm could ask about
 * `cut the tree with the pipe` was whether the machete was in the player's
 * hand, and the sentence the player typed was overruled by the one the arm
 * could check.
 *
 * D252: a body's ORDERS are the same table read for a different sentence —
 * what it was told, not what was done to it — and share the one search.
 *
 * D251: a BODY's arms are read the same way — `release tally` is TALLY's own
 * act, not the dock's. Only the refusal differs, and only because a body
 * already had one: with no arm holding, it answers exactly as it did before
 * it had arms, in its `verbNarratives` or the `verbOnActor` slot's — the
 * D232 refusal every verb on a body earns.
 *
 * @packageDocumentation
 */

import type { RuntimeState, VerbInteraction } from '@orpheus/content-schema';

import type { RuntimeAction } from '../actions/runtime-action.js';
import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';
import type { CommandInHand } from '../conditions/command-in-hand.js';

import { requireActor, requireItem } from '../bundle/bundle-lookups.js';
import { evaluateCondition } from '../conditions/evaluate-condition.js';
import { speakSlot } from '../defaults/default-responses.js';
import { resolveNarrativeLines } from '../narrative/conditional-text.js';
import { type ActionHandlerResult, completed, rejected } from './action-handler-result.js';
import { stampNarrativeLines, type TranscriptCursor } from './transcript-cursor.js';
import { withEffects } from './with-effects.js';

type ActAction = Extract<RuntimeAction, { type: 'act' }>;

/**
 * The refusal when no arm holds: a thing's `verb_unanswered`, or a body's
 * own refusal for the verb (D251).
 *
 * @param bundle - The loaded bundle
 * @param state - The state before the turn
 * @param action - The act
 * @param commandInHand - The sentence, for a body's readings
 * @returns The typed refusal
 */
const unanswered = (bundle: EngineGameBundle, state: RuntimeState, action: ActAction, commandInHand: CommandInHand): ActionHandlerResult => {
	const slotValues = { intent: action.verbToken.toUpperCase(), verb: action.verbToken };

	if (action.actorId === undefined) {
		return rejected(bundle, state, { itemId: action.itemId, type: 'verb_unanswered', verbToken: action.verbToken }, [
			speakSlot(bundle, state, 'verbUnanswered', { ...slotValues, itemName: requireItem(bundle, action.itemId).name }),
		]);
	}

	const actor = requireActor(bundle, action.actorId);
	const authoredLines = actor.verbNarratives?.[action.verbToken];

	return rejected(
		bundle,
		state,
		{ actorId: action.actorId, type: 'verb_on_actor', verbToken: action.verbToken },
		authoredLines === undefined
			? [speakSlot(bundle, state, 'verbOnActor', { ...slotValues, itemName: actor.name })]
			: resolveNarrativeLines(authoredLines, bundle, state, commandInHand),
	);
};

/**
 * The first arm that holds, spoken and applied — the one shape every authored
 * table shares (D196's verbs, D252's orders): its lines' readings chosen with
 * the command in hand and stamped under one key per (verb, owner), then its
 * effects folded.
 *
 * @param bundle - The loaded bundle
 * @param state - The state before the turn
 * @param arms - The arms for the verb, in authored order
 * @param commandInHand - The sentence the arms read
 * @param cursor - The turn's transcript cursor
 * @param stampKey - The voice index's key for this (verb, owner)
 * @returns The completed turn, or `undefined` when no arm holds
 */
const runFirstArmThatHolds = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	arms: readonly VerbInteraction[],
	commandInHand: CommandInHand,
	cursor: TranscriptCursor,
	stampKey: string,
): ActionHandlerResult | undefined => {
	const arm = arms.find((candidate) =>
		candidate.conditions.every((condition) => evaluateCondition(condition, bundle, state, commandInHand)),
	);

	if (arm === undefined) {
		return undefined;
	}

	const spoken = stampNarrativeLines(bundle, state, cursor, resolveNarrativeLines(arm.narratives, bundle, state, commandInHand), stampKey);

	return withEffects(bundle, completed(state, spoken), arm.effects, commandInHand);
};

/**
 * `act` — an authored verb applied to one thing, or (D251) one body.
 *
 * @param bundle - The loaded bundle
 * @param state - The state before the turn
 * @param action - The narrowed action (the canonical verb and what it is done to)
 * @param cursor - The turn's transcript cursor
 * @returns The arm's narration and effects, or the refusal
 */
export const handleAct = (bundle: EngineGameBundle, state: RuntimeState, action: ActAction, cursor: TranscriptCursor): ActionHandlerResult => {
	const owner = action.actorId === undefined ? requireItem(bundle, action.itemId) : requireActor(bundle, action.actorId);
	// The sentence that reached the thing: the thing itself is its direct
	// object by construction, and the other two slots arrived on the action.
	// A body names no item (D232), so its act has no direct object to ask after.
	const commandInHand: CommandInHand = {
		directObjectItemId: action.itemId,
		indirectObjectItemId: action.indirectObjectItemId,
		toolItemId: action.toolItemId,
	};

	// The same stamp shape as `use:`'s, so the voice index hears one act per
	// (verb, thing) pair however many arms it has.
	return (
		runFirstArmThatHolds(bundle, state, owner.verbInteractions?.[action.verbToken] ?? [], commandInHand, cursor, `act:${action.verbToken}>${action.actorId ?? action.itemId}`) ??
		unanswered(bundle, state, action, commandInHand)
	);
};

/**
 * `order` (D252) — a body told to do something: its `orders` arms for the
 * verb, read exactly as an authored verb's are, with the clause's things in
 * hand. No arm holding, the body says so in its `orderFallbackNarratives`,
 * or the `orderRefused` slot does.
 *
 * @param bundle - The loaded bundle
 * @param state - The state before the turn
 * @param action - The order: the body, the verb it was told, the clause's things
 * @param cursor - The turn's transcript cursor
 * @returns The arm's narration and effects, or the `order_unanswered` refusal
 */
export const handleOrder = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	action: Extract<RuntimeAction, { type: 'order' }>,
	cursor: TranscriptCursor,
): ActionHandlerResult => {
	const actor = requireActor(bundle, action.actorId);
	const commandInHand: CommandInHand = {
		directObjectItemId: action.directObjectItemId,
		indirectObjectItemId: action.indirectObjectItemId,
		toolItemId: action.toolItemId,
	};

	return (
		runFirstArmThatHolds(bundle, state, actor.orders?.[action.verbToken] ?? [], commandInHand, cursor, `order:${action.verbToken}>${action.actorId}`) ??
		rejected(
			bundle,
			state,
			{ actorId: action.actorId, type: 'order_unanswered', verbToken: action.verbToken },
			actor.orderFallbackNarratives === undefined
				? [speakSlot(bundle, state, 'orderRefused', { intent: action.verbToken.toUpperCase(), itemName: actor.name, verb: action.verbToken })]
				: resolveNarrativeLines(actor.orderFallbackNarratives, bundle, state, commandInHand),
		)
	);
};

/**
 * Route an action answered by an authored table to its reading: a verb's arms, or a body's orders.
 *
 * @param bundle - The loaded bundle
 * @param state - The state before the turn
 * @param action - The `act` or the `order`
 * @param cursor - The turn's transcript cursor
 * @returns The handler's result
 */
export const handleAuthoredTable = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	action: Extract<RuntimeAction, { type: 'act' | 'order' }>,
	cursor: TranscriptCursor,
): ActionHandlerResult => (action.type === 'act' ? handleAct(bundle, state, action, cursor) : handleOrder(bundle, state, action, cursor));
